/**
 * CodeBlock Component with Copy Functionality
 *
 * Renders code blocks with syntax highlighting and a copy-to-clipboard button
 * that appears on hover
 */

import React, { useState } from 'react';

interface CodeBlockProps {
  code: string;
  language?: string;
  inline?: boolean;
}

export const CodeBlock: React.FC<CodeBlockProps> = ({ code, language, inline = false }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  if (inline) {
    return (
      <code className="px-1.5 py-0.5 bg-slate-800 text-cyan-300 rounded text-sm font-mono">
        {code}
      </code>
    );
  }

  return (
    <div className="relative group my-4">
      {/* Copy button - appears on hover */}
      <button
        onClick={handleCopy}
        className="absolute right-2 top-2 px-3 py-1.5 bg-slate-700/80 hover:bg-slate-600/80 text-slate-300 hover:text-white rounded text-xs font-medium transition-all opacity-0 group-hover:opacity-100 flex items-center space-x-1.5 z-10"
        aria-label="Copy code to clipboard"
      >
        {copied ? (
          <>
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span>Copied!</span>
          </>
        ) : (
          <>
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <span>Copy</span>
          </>
        )}
      </button>

      {/* Language badge */}
      {language && (
        <div className="absolute left-2 top-2 px-2 py-0.5 bg-slate-800/80 text-slate-400 rounded text-xs font-medium">
          {language}
        </div>
      )}

      {/* Code content */}
      <pre className="bg-slate-900/50 border border-slate-700/50 rounded-lg p-4 pt-10 overflow-x-auto">
        <code className={`text-sm font-mono ${language ? `language-${language}` : ''}`}>
          {code}
        </code>
      </pre>
    </div>
  );
};
